import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import { memberTabAdminActionClick } from '@wix/bi-logger-groups/v2';

import { AvatarSize } from 'wix-ui-tpa';

import { IGroupMember } from 'api/members/types';
import { Role as GroupRole } from '@wix/ambassador-social-groups-v2-group-role/types';
import { selectCurrentUser } from 'store/application/selectors';
import { selectHasAdminRole } from 'store/groups/selectors';

import { BIUserEntry } from 'common/bi-logger/types';
import { UserCard } from 'common/components/Cards/UserCard';
import { AdminBadge } from 'common/components/AdminBadge';
import { MemberBadges } from 'common/components/MemberBadges';
import {
  BlackAndWhiteThreeDots,
  ThreeDotsActions,
} from 'common/components/BlackAndWhiteThreeDots';

import settingsParams from 'Group/settingsParams';

import { FollowButton } from './FollowButton';
import { ChangeMemberRoleDialog } from './ChangeMemberRoleDialog';
import { RemoveMemberDialog } from './RemoveMemberDialog';
import { MemberAnswersDialog } from './MemberAnswersDialog';

import { classes } from './Member.st.css';

interface IMemberProps {
  groupId: string;
  member: IGroupMember;
  bw?: boolean;
  children?: React.ReactNode;
}

export function Member(props: IMemberProps) {
  const { member, groupId } = props;

  const { t } = useTranslation();
  const bi = useBi();
  const settings = useSettings();
  const { isMobile } = useEnvironment();
  const user = useSelector(selectCurrentUser);
  const isAdmin = useSelector(selectHasAdminRole(groupId));

  const showBadges = settings.get(settingsParams.showMembersBadges);

  const [showMenu, setShowMenu] = useState(false);
  const [dialogs, setDialogs] = useState({
    answers: false,
    remove: false,
    changeRole: false,
  });

  const isMemberAdmin = member.role === GroupRole.ADMIN;
  const showActions = isAdmin && user.siteMemberId !== member.siteMemberId;

  const actions = useMemo<ThreeDotsActions[]>(
    () =>
      [
        {
          shown: !!member.answersCount,
          onClick: () => handleDialogShow('answers'),
          content: t('groups-web.members.more-actions.view-answers.menu-item'),
        },
        {
          shown: !isMemberAdmin,
          onClick: () => {
            handleDialogShow('changeRole');
            reportBi('set_admin', member.siteMemberId);
          },
          content: t(
            'groups-web.members.more-actions.add-admin-role.menu-item',
          ),
        },
        {
          shown: isMemberAdmin,
          onClick: () => {
            handleDialogShow('changeRole');
            reportBi('remove_admin', member.siteMemberId);
          },
          content: t(
            'groups-web.members.more-actions.remove-admin-role.menu-item',
          ),
        },
        {
          shown: true,
          onClick: () => {
            handleDialogShow('remove');
            reportBi('remove_member', member.siteMemberId);
          },
          content: t(
            'groups-web.members.more-actions.remove-member-from-group.menu-item',
          ),
        },
      ].filter((action) => action.shown),
    [member.role],
  );

  return (
    <>
      <UserCard
        className={classes.root}
        profile={member}
        showBadges={false}
        avatarSize={isMobile ? AvatarSize.medium : AvatarSize.large}
        action={
          !isMobile ? (
            <div className={classes.actions}>
              <FollowButton
                isPrivate={member.isPrivate}
                memberId={member.siteMemberId as string}
              />
              {showActions && (
                <BlackAndWhiteThreeDots
                  isOpen={showMenu}
                  onOpen={handleMenuOpen}
                  onClose={handleMenuClose}
                  items={actions}
                />
              )}
            </div>
          ) : null
        }
      >
        <div>
          {isMemberAdmin ? <AdminBadge /> : null}
          {showBadges ? <MemberBadges badges={member.badges} /> : null}
        </div>
      </UserCard>
      <ChangeMemberRoleDialog
        groupId={groupId}
        member={member}
        isOpen={dialogs.changeRole}
        onClose={handleDialogClose('changeRole')}
      />
      <RemoveMemberDialog
        groupId={groupId}
        member={member}
        isOpen={dialogs.remove}
        onClose={handleDialogClose('remove')}
      />
      <MemberAnswersDialog
        groupId={groupId}
        member={member}
        isOpen={dialogs.answers}
        onClose={handleDialogClose('answers')}
      />
    </>
  );

  function handleDialogShow(dialog: keyof typeof dialogs) {
    setDialogs((dialogs) => ({ ...dialogs, [dialog]: true }));
    handleMenuClose();
  }

  function handleDialogClose(dialog: keyof typeof dialogs) {
    return () => setDialogs((dialogs) => ({ ...dialogs, [dialog]: false }));
  }

  function handleMenuOpen() {
    setShowMenu(true);
  }

  function handleMenuClose() {
    setShowMenu(false);
  }

  function reportBi(action: string, memberId?: string) {
    bi.report(
      memberTabAdminActionClick({
        action,
        group_id: groupId,
        origin: 'member_tab_member_option_clk',
        userEntry: BIUserEntry.SITE,
        site_member_id: memberId,
      }),
    );
  }
}

Member.displayName = 'Member';

export function MemberSkeleton() {
  const { isMobile } = useEnvironment();

  return (
    <UserCard
      loading
      avatarSize={isMobile ? AvatarSize.medium : AvatarSize.large}
    />
  );
}
